// src/services/data/fetchers/categoryFetchers.ts
/**
 * Async fetchers for category/sector data.
 * Used inside React Query's queryFn where hooks cannot be used.
 * 
 * These parallel the hooks in useCategoryMetrics.ts but are pure async functions.
 */

import {
  fetchCategoryCurrent,
  fetchCategoryCurrentChanges,
  fetchCategoryHistoryCompact,
} from '@/services/queries/dashboards/categoriesQueries';
import { getCategoryIdMap } from '../hooks/useCategoryMetrics';
import type { TimeSeriesData } from './transformations';

// ─────────────────────────────────────────────────────────────────────────────
// Current Values
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch current metric values for categories.
 * Uses the shared category ID map.
 */
export async function fetchCategoryCurrentValues(
  categories: string[],
  metricKey: string
): Promise<Record<string, number>> {
  if (categories.length === 0) return {};

  const categoryMap = await getCategoryIdMap();
  const allCurrent = await fetchCategoryCurrent({});
  const result: Record<string, number> = {};

  for (const catCode of categories) {
    const categoryId = categoryMap.get(catCode.toLowerCase());
    if (!categoryId) continue;

    const catData = allCurrent.find(c => c.category_id === categoryId);
    result[catCode] = catData?.metrics[metricKey] ?? 0;
  }

  return result;
}

/**
 * Fetch change values for categories over a window.
 */
export async function fetchCategoryChangeValues(
  categories: string[],
  metricKey: string,
  window: string
): Promise<Record<string, number>> {
  if (categories.length === 0) return {};

  const categoryMap = await getCategoryIdMap();
  const allChanges = await fetchCategoryCurrentChanges({});
  const result: Record<string, number> = {};

  for (const catCode of categories) {
    const categoryId = categoryMap.get(catCode.toLowerCase());
    if (!categoryId) continue;

    const catData = allChanges.find(c => c.category_id === categoryId);
    result[catCode] = catData?.metrics[metricKey]?.[window]?.value ?? 0;
  }

  return result;
}

// ─────────────────────────────────────────────────────────────────────────────
// Historical Data
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch historical time series for multiple categories.
 * Returns aligned dates and series.
 */
export async function fetchCategoryHistoricalData(
  categories: string[],
  metricKey: string
): Promise<TimeSeriesData> {
  if (categories.length === 0) return { dates: [], series: [] };

  const categoryMap = await getCategoryIdMap();

  const seriesPromises = categories.map(async (catCode) => {
    const categoryId = categoryMap.get(catCode.toLowerCase());
    if (!categoryId) return null;

    const result = await fetchCategoryHistoryCompact({
      categoryId,
      metricKeys: [metricKey],
    });

    const metricData = result.metrics?.find(m => m.metric_key === metricKey);
    if (!metricData) return null;

    return {
      name: catCode,
      dates: metricData.dates?.map(d => String(d)) ?? [],
      values: (metricData.values ?? []).map(v => v ?? 0),
    };
  });

  const seriesResults = await Promise.all(seriesPromises);
  const validSeries = seriesResults.filter((s): s is NonNullable<typeof s> => s !== null);

  if (validSeries.length === 0) return { dates: [], series: [] };

  // Use first series dates as baseline (all categories should align)
  const dates = validSeries[0]?.dates ?? [];
  const series = validSeries.map(s => ({ name: s.name, values: s.values }));

  return { dates, series };
}

